import { useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { CheckCircle2, ChevronRight } from 'lucide-react'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Button, buttonClass } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { EmptyState } from '@/components/ui/EmptyState'
import { MoneyText } from '@/components/ui/MoneyText'
import { PageHeader } from '@/components/ui/PageHeader'
import { Sheet } from '@/components/ui/Sheet'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { Textarea } from '@/components/ui/Textarea'
import { useToast } from '@/components/ui/Toast'
import { useProfile } from '@/auth/SessionProvider'
import { listWaitingBudgets } from '@/data/budgets'
import type { WaitingBudget } from '@/data/budgets'
import { approveExpense, listWaitingExpenses, rejectExpense } from '@/data/expenses'
import { formatDay } from '@/lib/format'
import { formatKwacha } from '@/lib/money'
import { cn } from '@/lib/cn'
import { quarterLabel } from '@/features/budgets/schemas'
import { relativeTime } from '@/features/home/insights'
import { ProofPhotos } from '@/features/proofs/ProofPhotos'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

type WaitingExpense = Awaited<ReturnType<typeof listWaitingExpenses>>[number]
type Tab = 'all' | 'budgets' | 'expenses'

function Failed({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <div>
      <p role="alert" className="text-body text-bad">{errText(error)}</p>
      <Button variant="secondary" size="lg" className="mt-3" onClick={retry}>Try again</Button>
    </div>
  )
}

/** One fact in an opened row: a small label over its value. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-label text-muted">{label}</dt>
      <dd className="text-body text-ink">{children}</dd>
    </div>
  )
}

/** Central treasury's queue: everything sent for a decision, across every department. */
export function ApprovalsPage() {
  const profile = useProfile()
  const canDecide = profile.isAdmin
  const toast = useToast()
  const qc = useQueryClient()
  const budgetsQ = useQuery({ queryKey: ['waiting-budgets'], queryFn: listWaitingBudgets })
  const expensesQ = useQuery({ queryKey: ['waiting-expenses'], queryFn: listWaitingExpenses })

  const [tab, setTab] = useState<Tab>('all')
  const [opened, setOpened] = useState<string | null>(null)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [sendBack, setSendBack] = useState<WaitingExpense | null>(null)
  const [reason, setReason] = useState('')
  const [reasonError, setReasonError] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirmBulk, setConfirmBulk] = useState(false)

  const budgets = budgetsQ.data ?? []
  const expenses = expensesQ.data ?? []
  const showBudgets = tab !== 'expenses'
  const showExpenses = tab !== 'budgets'
  const loading = (showBudgets && budgetsQ.isPending) || (showExpenses && expensesQ.isPending)
  const failedQ = (showBudgets && budgetsQ.isError ? budgetsQ : null) ?? (showExpenses && expensesQ.isError ? expensesQ : null)
  const nothing = (!showBudgets || budgets.length === 0) && (!showExpenses || expenses.length === 0)

  const pickedExpenses = expenses.filter((x) => picked.has(x.id))
  const pickedTotal = pickedExpenses.reduce((s, x) => s + x.amountNgwee, 0)

  const refreshAll = () => qc.invalidateQueries()
  const toggle = (id: string) =>
    setPicked((p) => {
      const n = new Set(p)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })

  const approveOne = async (x: WaitingExpense) => {
    if (busy) return
    setBusy(true)
    try {
      await approveExpense(x.id)
      await refreshAll()
      toast.success(`Approved: ${x.departmentName}, ${x.payee}, ${formatKwacha(x.amountNgwee)}.`)
      setPicked((p) => {
        const n = new Set(p)
        n.delete(x.id)
        return n
      })
    } catch (e) {
      toast.error(errText(e))
    } finally {
      setBusy(false)
    }
  }

  // Each one is approved on its own, so one that cannot be approved never holds up the rest.
  const approveSelected = async () => {
    setConfirmBulk(false)
    if (busy) return
    setBusy(true)
    const done: string[] = []
    const failed: { x: WaitingExpense; why: string }[] = []
    for (const x of pickedExpenses) {
      try {
        await approveExpense(x.id)
        done.push(x.id)
      } catch (e) {
        failed.push({ x, why: errText(e) })
      }
    }
    await refreshAll()
    setPicked(new Set(failed.map((f) => f.x.id)))
    setBusy(false)
    if (done.length > 0) toast.success(`Approved ${done.length} ${done.length === 1 ? 'expense' : 'expenses'}.`)
    if (failed.length > 0) toast.error(`${failed.length} could not be approved. ${failed[0].x.payee}: ${failed[0].why}`)
  }

  const submitSendBack = async (e: FormEvent) => {
    e.preventDefault()
    if (!sendBack || busy) return
    if (!reason.trim()) return setReasonError('Say why it is being sent back.')
    setBusy(true)
    setReasonError('')
    try {
      await rejectExpense(sendBack.id, reason)
      await refreshAll()
      toast.success(`Sent back to ${sendBack.departmentName} with your reason.`)
      setPicked((p) => {
        const n = new Set(p)
        n.delete(sendBack.id)
        return n
      })
      setSendBack(null)
      setReason('')
    } catch (err) {
      setReasonError(errText(err))
    } finally {
      setBusy(false)
    }
  }

  const tabs: { id: Tab; label: string; count: number | null }[] = [
    { id: 'all', label: 'All', count: budgetsQ.data && expensesQ.data ? budgets.length + expenses.length : null },
    { id: 'budgets', label: 'Budgets', count: budgetsQ.data ? budgets.length : null },
    { id: 'expenses', label: 'Expenses', count: expensesQ.data ? expenses.length : null },
  ]

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Approvals"
        description={canDecide ? 'Open an item to approve it or send it back with a reason.' : 'What is waiting for central treasury. You can look but not decide.'}
      />

      <div role="tablist" aria-label="What is waiting" className="flex gap-1 overflow-x-auto border-b border-line">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              '-mb-px flex min-h-[44px] shrink-0 items-center gap-2 border-b-2 px-3 text-body font-medium transition-colors',
              tab === t.id ? 'border-brand text-brand-ink' : 'border-transparent text-muted hover:text-ink',
            )}
          >
            {t.label}
            {t.count !== null && (
              <Badge tone={t.count > 0 ? 'warning' : 'neutral'}>
                {t.count}
                <span className="sr-only sm:not-sr-only"> waiting</span>
              </Badge>
            )}
          </button>
        ))}
      </div>

      <Card>
        <CardBody>
          {loading ? (
            <SkeletonRows rows={4} />
          ) : failedQ ? (
            <Failed error={failedQ.error} retry={() => void failedQ.refetch()} />
          ) : nothing ? (
            <EmptyState
              icon={CheckCircle2}
              title={tab === 'budgets' ? 'No budgets are waiting' : tab === 'expenses' ? 'No expenses are waiting' : 'No budgets or expenses are waiting'}
              description="Nothing needs a decision right now."
            />
          ) : (
            <ul className="divide-y divide-line" aria-label="Waiting for a decision">
              {showBudgets && budgets.map((b) => (
                <BudgetRow key={b.id} b={b} open={opened === `b-${b.id}`} onToggle={() => setOpened(opened === `b-${b.id}` ? null : `b-${b.id}`)} />
              ))}
              {showExpenses && expenses.map((x) => (
                <ExpenseRow
                  key={x.id}
                  x={x}
                  canDecide={canDecide}
                  open={opened === `x-${x.id}`}
                  checked={picked.has(x.id)}
                  busy={busy}
                  onToggle={() => setOpened(opened === `x-${x.id}` ? null : `x-${x.id}`)}
                  onCheck={() => toggle(x.id)}
                  onApprove={() => void approveOne(x)}
                  onSendBack={() => {
                    setReason('')
                    setReasonError('')
                    setSendBack(x)
                  }}
                />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {canDecide && pickedExpenses.length > 1 && (
        <div className="sticky bottom-4 z-30 flex flex-wrap items-center justify-between gap-3 rounded-card border border-line-strong bg-surface px-4 py-3 shadow-lg" role="region" aria-label="Selected expenses">
          <p className="text-body font-medium text-ink">
            {pickedExpenses.length} expenses selected · <span className="tabular-nums">{formatKwacha(pickedTotal)}</span>
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => setPicked(new Set())}>Clear</Button>
            <Button onClick={() => setConfirmBulk(true)} isLoading={busy}>Approve selected</Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmBulk}
        onCancel={() => setConfirmBulk(false)}
        onConfirm={() => void approveSelected()}
        title={`Approve ${pickedExpenses.length} expenses totalling ${formatKwacha(pickedTotal)}?`}
        confirmLabel="Approve them all"
      >
        Each one is approved on its own. If one cannot be approved, the others still go through and you are told which.
      </ConfirmDialog>

      <Sheet isOpen={sendBack !== null} onClose={() => setSendBack(null)} title="Send this back">
        <form onSubmit={(e) => void submitSendBack(e)} className="flex flex-col gap-4" noValidate>
          {sendBack && (
            <p className="text-body text-ink">
              {sendBack.departmentName}: {sendBack.payee}, {formatKwacha(sendBack.amountNgwee)}. The department sees your reason and can correct it and send it again.
            </p>
          )}
          <Textarea label="Why is it being sent back?" value={reason} onChange={(e) => setReason(e.target.value)} error={reasonError} rows={3} />
          <Button type="submit" size="lg" variant="danger" isLoading={busy}>Send back</Button>
        </form>
      </Sheet>
    </div>
  )
}

function BudgetRow({ b, open, onToggle }: { b: WaitingBudget; open: boolean; onToggle: () => void }) {
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-start gap-3">
        <button type="button" onClick={onToggle} aria-expanded={open} className="-ml-1 flex min-w-0 flex-1 items-start gap-3 rounded-control p-1 text-left hover:bg-page">
          <ChevronRight size={16} aria-hidden className={cn('mt-2.5 shrink-0 text-muted transition-transform', open && 'rotate-90')} />
          <Avatar name={b.departmentName} />
          <span className="min-w-0">
            <span className="block font-medium text-ink">{b.departmentName}</span>
            <span className="block text-body text-muted">
              {quarterLabel(b.quarterId)} · {b.lineCount} {b.lineCount === 1 ? 'line' : 'lines'}
              {b.revision > 1 ? ` · revision ${b.revision}` : ''}
            </span>
            {b.submittedAt && <span className="block text-label text-muted">Sent {relativeTime(b.submittedAt)}</span>}
          </span>
        </button>
        <div className="flex shrink-0 items-center gap-3">
          <Badge tone="info">Budget</Badge>
          <span className="min-w-[6rem] text-right font-semibold tabular-nums text-ink"><MoneyText ngwee={b.totalNgwee} strongZero /></span>
          <Link to="/d/$deptId/budgets/$quarterId" params={{ deptId: b.departmentId, quarterId: b.quarterId }} className={buttonClass('primary', 'sm')}>
            Review
          </Link>
        </div>
      </div>
      {open && (
        <dl className="mt-3 grid grid-cols-2 gap-3 rounded-control bg-page p-3 sm:grid-cols-4">
          <Fact label="Quarter">{quarterLabel(b.quarterId)}</Fact>
          <Fact label="Lines">{b.lineCount}</Fact>
          <Fact label="Revision">{b.revision}</Fact>
          <Fact label="Total"><MoneyText ngwee={b.totalNgwee} strongZero /></Fact>
          <div className="col-span-full text-body text-muted">A budget is approved after you have looked at every line, so open it with Review to decide.</div>
        </dl>
      )}
    </li>
  )
}

function ExpenseRow({
  x, canDecide, open, checked, busy, onToggle, onCheck, onApprove, onSendBack,
}: {
  x: WaitingExpense
  canDecide: boolean
  open: boolean
  checked: boolean
  busy: boolean
  onToggle: () => void
  onCheck: () => void
  onApprove: () => void
  onSendBack: () => void
}) {
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-start gap-3">
        {canDecide && (
          <input
            type="checkbox"
            checked={checked}
            onChange={onCheck}
            aria-label={`Select ${x.departmentName}: ${x.payee}`}
            className="mt-3 h-5 w-5 shrink-0 accent-[var(--color-brand)]"
          />
        )}
        <button type="button" onClick={onToggle} aria-expanded={open} className="-ml-1 flex min-w-0 flex-1 items-start gap-3 rounded-control p-1 text-left hover:bg-page">
          <ChevronRight size={16} aria-hidden className={cn('mt-2.5 shrink-0 text-muted transition-transform', open && 'rotate-90')} />
          <Avatar name={x.departmentName} />
          <span className="min-w-0">
            <span className="block font-medium text-ink">{x.departmentName}: {x.payee}</span>
            <span className="block text-body text-ink">{x.description}</span>
            <span className="block text-body text-muted">
              {formatDay(x.incurredOn)} · {x.categoryName}
              {x.budgetLineName ? ` · ${x.budgetLineName}` : ' · Not in the budget'}
            </span>
          </span>
        </button>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <Badge tone="neutral">Expense</Badge>
          <span className="min-w-[6rem] text-right font-semibold tabular-nums text-ink"><MoneyText ngwee={x.amountNgwee} strongZero /></span>
          {canDecide && (
            <>
              <Button size="sm" onClick={onApprove} disabled={busy}>Approve</Button>
              <Button size="sm" variant="secondary" onClick={onSendBack} disabled={busy}>Send back</Button>
            </>
          )}
          <Link to="/d/$deptId/expenses" params={{ deptId: x.departmentId }} className={buttonClass('ghost', 'sm')}>
            Review
          </Link>
        </div>
      </div>
      {open && (
        <div className="mt-3 rounded-control bg-page p-3">
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Fact label="Paid to">{x.payee}</Fact>
            <Fact label="Date">{formatDay(x.incurredOn)}</Fact>
            <Fact label="Category">{x.categoryName}</Fact>
            <Fact label="Budget line">{x.budgetLineName ?? 'Not in the budget'}</Fact>
            {x.receiptNo && <Fact label="Receipt number">{x.receiptNo}</Fact>}
          </dl>
          <ProofPhotos parent={{ kind: 'expense', id: x.id }} departmentId={x.departmentId} date={x.incurredOn} canAdd={false} noun="receipt" />
        </div>
      )}
    </li>
  )
}
